/**
 * Notification API
 *
 * Mock API for notification management.
 */

import { mockDb } from './mockDb'
import { delay, success, error } from './client'
import type { ApiResponse, PaginatedResponse, UUID } from '../types'
import type {
  Notification,
  NotificationPreferences,
  NotificationCounts,
  NotificationCategory,
} from '../types/notification.types'
import {
  COPPA_BLOCKED_CATEGORIES_UNDER_13,
  coppaDefaultPreferencesUnder13,
} from '../types/notification.types'

/**
 * Get current user ID from session storage
 */
function getCurrentUserId(): string | null {
  const stored = localStorage.getItem('docurious-session')
  if (!stored) return null
  try {
    return JSON.parse(stored).userId || null
  } catch {
    return null
  }
}

/**
 * Check if the current user is under 13 (COPPA-relevant).
 * Reads from the persisted auth store in localStorage.
 */
function isCurrentUserUnder13(): boolean {
  try {
    const authData = localStorage.getItem('docurious-auth')
    if (!authData) return false
    const parsed = JSON.parse(authData)
    return parsed?.state?.user?.isUnder13 === true
  } catch {
    return false
  }
}

/**
 * Filter notifications based on COPPA age restrictions.
 * Under-13 users do not receive social_activity, community_activity, or vendor notifications.
 */
function applyCoppaFilter(notifications: Notification[]): Notification[] {
  if (!isCurrentUserUnder13()) return notifications
  return notifications.filter(
    (n) => !COPPA_BLOCKED_CATEGORIES_UNDER_13.includes(n.category)
  )
}

export const notificationApi = {
  /**
   * Get notifications for current user
   */
  async getNotifications(
    page = 1,
    limit = 20,
    unreadOnly = false
  ): Promise<PaginatedResponse<Notification>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return {
        success: true,
        data: [],
        pagination: { page: 1, limit, total: 0, totalPages: 0, hasMore: false },
      }
    }

    let notifications = applyCoppaFilter(
      mockDb.findWhere<Notification>('notifications', (n) => n.userId === userId)
    )

    if (unreadOnly) {
      notifications = notifications.filter((n) => !n.isRead)
    }

    // Sort by most recent
    notifications.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )

    // Paginate
    const total = notifications.length
    const totalPages = Math.ceil(total / limit)
    const start = (page - 1) * limit
    const paginatedResults = notifications.slice(start, start + limit)

    return {
      success: true,
      data: paginatedResults,
      pagination: {
        page,
        limit,
        total,
        totalPages,
        hasMore: page < totalPages,
      },
    }
  },

  /**
   * Get notification counts
   */
  async getNotificationCounts(): Promise<ApiResponse<NotificationCounts>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return success({ total: 0, unread: 0, byCategory: {} })
    }

    const notifications = applyCoppaFilter(
      mockDb.findWhere<Notification>('notifications', (n) => n.userId === userId)
    )

    const unread = notifications.filter((n) => !n.isRead)

    const byCategory: Partial<Record<NotificationCategory, number>> = {}
    unread.forEach((n) => {
      byCategory[n.category] = (byCategory[n.category] || 0) + 1
    })

    return success({
      total: notifications.length,
      unread: unread.length,
      byCategory,
    })
  },

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: UUID): Promise<ApiResponse<Notification>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const notification = mockDb.findById<Notification>('notifications', notificationId)

    if (!notification) {
      return error('Notification not found')
    }

    if (notification.userId !== userId) {
      return error('Not authorized')
    }

    const updated = mockDb.update<Notification>('notifications', notificationId, {
      isRead: true,
      readAt: new Date().toISOString(),
    })

    return updated ? success(updated) : error('Failed to update notification')
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<ApiResponse<void>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const notifications = mockDb.findWhere<Notification>(
      'notifications',
      (n) => n.userId === userId && !n.isRead
    )

    notifications.forEach((n) => {
      mockDb.update<Notification>('notifications', n.id, {
        isRead: true,
        readAt: new Date().toISOString(),
      })
    })

    return success(undefined)
  },

  /**
   * Delete a notification
   */
  async deleteNotification(notificationId: UUID): Promise<ApiResponse<void>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    const notification = mockDb.findById<Notification>('notifications', notificationId)

    if (!notification) {
      return error('Notification not found')
    }

    if (notification.userId !== userId) {
      return error('Not authorized')
    }

    mockDb.delete('notifications', notificationId)
    return success(undefined)
  },

  /**
   * Get notification preferences
   */
  async getPreferences(): Promise<ApiResponse<NotificationPreferences>> {
    await delay(50)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    let prefs = mockDb.findOne<NotificationPreferences>(
      'notificationPreferences',
      (p) => p.userId === userId
    )

    if (!prefs) {
      // Use COPPA-safe defaults for under-13 users
      const defaults = isCurrentUserUnder13()
        ? { userId, ...coppaDefaultPreferencesUnder13 }
        : {
            userId,
            inAppEnabled: true,
            emailEnabled: true,
            pushEnabled: false,
            categoryPreferences: {
              challenge_updates: { inApp: true, email: true, push: false },
              social_activity: { inApp: true, email: false, push: false },
              community_activity: { inApp: true, email: false, push: false },
              reminders: { inApp: true, email: true, push: false },
              digests: { inApp: false, email: true, push: false },
              system: { inApp: true, email: true, push: false },
              gifts: { inApp: true, email: true, push: false },
              verification: { inApp: true, email: true, push: false },
              teacher_feedback: { inApp: true, email: true, push: false },
              gamification: { inApp: true, email: false, push: false },
              parent_alerts: { inApp: true, email: true, push: false },
              school_admin: { inApp: true, email: true, push: false },
              vendor: { inApp: true, email: true, push: false },
            },
            quietHoursEnabled: false,
            quietHoursStart: '22:00',
            quietHoursEnd: '08:00',
            quietHoursTimezone: 'America/New_York',
            emailDigestFrequency: 'daily' as const,
          }

      prefs = mockDb.insert('notificationPreferences', defaults) as unknown as NotificationPreferences
    }

    return success(prefs)
  },

  /**
   * Update notification preferences
   */
  async updatePreferences(
    updates: Partial<NotificationPreferences>
  ): Promise<ApiResponse<NotificationPreferences>> {
    await delay(100)

    const userId = getCurrentUserId()
    if (!userId) {
      return error('Not authenticated')
    }

    let prefs = mockDb.findOne<NotificationPreferences>(
      'notificationPreferences',
      (p) => p.userId === userId
    )

    if (!prefs) {
      // Create with updates
      const newPrefs = {
        userId,
        inAppEnabled: true,
        emailEnabled: true,
        pushEnabled: false,
        categoryPreferences: {
          challenge_updates: { inApp: true, email: true, push: false },
          social_activity: { inApp: true, email: false, push: false },
          community_activity: { inApp: true, email: false, push: false },
          reminders: { inApp: true, email: true, push: false },
          digests: { inApp: false, email: true, push: false },
          system: { inApp: true, email: true, push: false },
          gifts: { inApp: true, email: true, push: false },
          verification: { inApp: true, email: true, push: false },
          teacher_feedback: { inApp: true, email: true, push: false },
        },
        quietHoursEnabled: false,
        quietHoursStart: '22:00',
        quietHoursEnd: '08:00',
        quietHoursTimezone: 'America/New_York',
        emailDigestFrequency: 'daily' as const,
        ...updates,
      }

      prefs = mockDb.insert('notificationPreferences', newPrefs) as unknown as NotificationPreferences
      return success(prefs)
    }

    const updated = mockDb.update(
      'notificationPreferences',
      prefs.id!,
      updates
    ) as unknown as NotificationPreferences

    return updated ? success(updated) : error('Failed to update preferences')
  },

  /**
   * Create a notification (internal use)
   */
  async createNotification(
    data: Omit<Notification, 'id' | 'createdAt' | 'updatedAt' | 'isRead' | 'readAt' | 'sentViaEmail' | 'sentViaPush'>
  ): Promise<ApiResponse<Notification>> {
    await delay(50)

    const notification = mockDb.insert<Notification>('notifications', {
      ...data,
      isRead: false,
      sentViaEmail: false,
      sentViaPush: false,
    })

    return success(notification)
  },
}
